import { motion } from 'framer-motion'

/**
 * Services Component
 * Four main services with modern, elegant design
 */
const Services = () => {
  const services = [
    {
      number: '1',
      title: 'Pharmaceutical Packaging Artwork',
      description: 'We deliver regulatory‑compliant artwork for pharmaceutical cartons, blister packs, labels, and inserts. Every file is aligned with FDA, CDSCO, EU, and GCC standards, ensuring accuracy, legibility, and audit‑ready precision across all components.',
      features: [
        'Regulatory panel creation and formatting',
        'Multilingual artwork (Arabic, Hindi, French, and more)',
        'Braille integration and patient‑safety icon systems',
        'Print‑ready files for vendors and CMOs',
        'Artwork harmonization across SKUs and global regions',
      ],
      accent: 'blue',
    },
    {
      number: '2',
      title: 'Food Packaging Artwork',
      description: 'Our food packaging artwork is designed for clarity, compliance, and consumer trust. From nutritional panels to allergen declarations, we ensure every detail meets regional regulations and cultural expectations.',
      features: [
        'Nutritional labeling and ingredient hierarchy',
        'Allergen and dietary icon systems (vegan, gluten free, etc.)',
        'Region specific compliance (FSSAI, EU, GCC)',
        'Multilingual adaptation for export markets',
        'Visual consistency across product families',
      ],
      accent: 'green',
    },
    {
      number: '3',
      title: 'FMCG Packaging Artwork',
      description: 'FMCG brands require speed without sacrificing consistency. We deliver scalable, brand aligned artwork for personal care, household, and lifestyle products — ready for rapid market deployment.',
      features: [
        'Master artwork creation for multi SKU portfolios',
        'Barcode placement and regulatory symbol accuracy',
        'Brand adaptation across formats, sizes, and substrates',
        'Vendor coordination for print and production',
        'Icon systems for product features and claims',
      ],
      accent: 'orange',
    },
    {
      number: '4',
      title: 'Global Market Localization',
      description: 'We ensure your packaging communicates effectively across borders. Our multilingual localization services maintain compliance, cultural relevance, and visual consistency for global markets.',
      features: [
        'Translation coordination and layout adaptation',
        'Arabic packaging expertise (right to left formatting)',
        'Cultural iconography and color sensitivity',
        'Regulatory alignment for export regions',
        'Harmonized artwork for global rollouts',
      ],
      accent: 'purple',
    },
  ]

  const getAccentClasses = (accent) => {
    const classes = {
      blue: {
        badge: 'bg-blue-600',
        icon: 'bg-blue-100 text-blue-700',
        border: 'border-blue-100',
      },
      green: {
        badge: 'bg-green-600',
        icon: 'bg-green-100 text-green-700',
        border: 'border-green-100',
      },
      orange: {
        badge: 'bg-orange-600',
        icon: 'bg-orange-100 text-orange-700',
        border: 'border-orange-100',
      },
      purple: {
        badge: 'bg-purple-600',
        icon: 'bg-purple-100 text-purple-700',
        border: 'border-purple-100',
      },
    }
    return classes[accent] || classes.blue
  }

  return (
    <section
      id="services"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 via-white to-gray-50 border-t border-gray-200/50 relative overflow-hidden"
      aria-labelledby="services-heading"
    >
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2
            id="services-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Services – What We Deliver
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto"></div>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const accentClasses = getAccentClasses(service.accent)
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -12, scale: 1.02, transition: { duration: 0.3 } }}
                className="group"
              >
                <div className="h-full bg-white rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden relative">
                  {/* Animated gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.accent === 'blue' ? 'from-blue-50' : service.accent === 'green' ? 'from-green-50' : service.accent === 'orange' ? 'from-orange-50' : 'from-purple-50'} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  {/* Top accent bar */}
                  <div className={`h-2 ${accentClasses.badge} group-hover:h-3 transition-all duration-300`}></div>
                  
                  <div className="p-8 lg:p-10 relative z-10">
                    {/* Number and Title */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className={`flex-shrink-0 w-16 h-16 ${accentClasses.badge} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                        <span className="text-2xl font-black text-white">{service.number}</span>
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 pt-2 leading-tight group-hover:text-primary transition-colors duration-300">
                        {service.title}
                      </h3>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                      {service.description}
                    </p>
                    
                    {/* Features List */}
                    <div className="space-y-4">
                      {service.features.map((feature, featureIndex) => (
                        <motion.div
                          key={featureIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: (index * 0.1) + (featureIndex * 0.05) }}
                          className="flex items-start gap-4 group/item"
                        >
                          <div className={`flex-shrink-0 mt-1 w-7 h-7 ${accentClasses.icon} rounded-lg flex items-center justify-center group-hover/item:scale-110 group-hover/item:rotate-12 transition-all duration-300`}>
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <span className="text-gray-700 leading-relaxed flex-1 pt-0.5">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
      
      {/* Bottom decorative divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent"></div>
    </section>
  )
}

export default Services
